export type InspectionWorkSessionRecord = {
  id: string;
  userId: string;
  startedAt: string;
  endedAt?: string;
  lastActivityAt: string;
  latitude?: number;
  longitude?: number;
};

export type InspectionWorkSessionCoordinates = {
  latitude: number;
  longitude: number;
};

function hasSubmitCoordinates(
  coordinates: InspectionWorkSessionCoordinates | undefined
): coordinates is InspectionWorkSessionCoordinates {
  if (!coordinates) {
    return false;
  }
  const { latitude, longitude } = coordinates;
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

export function getOpenInspectionWorkSession(
  workSessions: InspectionWorkSessionRecord[] | undefined
): InspectionWorkSessionRecord | undefined {
  if (!workSessions?.length) {
    return undefined;
  }
  return workSessions.find((session) => !session.endedAt);
}

export function openInspectionWorkSession(params: {
  workSessions?: InspectionWorkSessionRecord[];
  userId: string;
  nowIso?: string;
}): InspectionWorkSessionRecord[] {
  const nowIso = params.nowIso ?? new Date().toISOString();
  const sessions = [...(params.workSessions ?? [])];
  const openSession = getOpenInspectionWorkSession(sessions);
  if (openSession) {
    openSession.lastActivityAt = nowIso;
    return sessions;
  }
  sessions.push({
    id: crypto.randomUUID(),
    userId: params.userId,
    startedAt: nowIso,
    lastActivityAt: nowIso,
  });
  return sessions;
}

export function closeOpenInspectionWorkSession(
  workSessions: readonly InspectionWorkSessionRecord[] | undefined,
  endedAtIso: string,
  coordinates?: InspectionWorkSessionCoordinates
): InspectionWorkSessionRecord[] {
  const submitCoordinates = hasSubmitCoordinates(coordinates) ? coordinates : undefined;
  let hasClosedOpenSession = false;
  return (workSessions ?? []).map((session) => {
    if (hasClosedOpenSession || session.endedAt) {
      return { ...session };
    }
    hasClosedOpenSession = true;
    return {
      ...session,
      endedAt: endedAtIso,
      lastActivityAt: endedAtIso,
      ...(submitCoordinates
        ? { latitude: submitCoordinates.latitude, longitude: submitCoordinates.longitude }
        : {}),
    };
  });
}

export function touchInspectionWorkSessionActivity(
  workSessions: InspectionWorkSessionRecord[] | undefined,
  nowIso: string = new Date().toISOString()
): InspectionWorkSessionRecord[] | undefined {
  const openSession = getOpenInspectionWorkSession(workSessions);
  if (!openSession) {
    return workSessions;
  }
  return (workSessions ?? []).map((session) =>
    session.id === openSession.id ? { ...session, lastActivityAt: nowIso } : session
  );
}
